import type { SavedState, RestoredImage, StashItem, SliderConfig, GaSliderConfig } from './types.js';
import { IDB_NAME, IDB_VER, LS_KEY } from './dom.js';

/**
 * Open (or create) the application IndexedDB database.
 * Creates the "image" object store on first run / version upgrade.
 */
export function idbOpen(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(IDB_NAME, IDB_VER);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains('image')) db.createObjectStore('image');
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

/**
 * Write a key-value pair into the "image" object store.
 */
export async function idbPut(key: string, value: unknown): Promise<void> {
  const db = await idbOpen();
  return new Promise((resolve, reject) => {
    const tx = db.transaction('image', 'readwrite');
    tx.objectStore('image').put(value, key);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

/**
 * Read a value from the "image" object store by key.
 */
export async function idbGet(key: string): Promise<unknown> {
  const db = await idbOpen();
  return new Promise((resolve, reject) => {
    const tx = db.transaction('image', 'readonly');
    const req = tx.objectStore('image').get(key);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

/**
 * Serialize the current application state to localStorage.
 * Reads slider values from their DOM input elements.
 */
export function saveState(state: {
  seedX: number | null;
  seedY: number | null;
  stash: StashItem[];
  stashCounter: number;
  sliders: Record<string, SliderConfig>;
  gaSliders: Record<string, GaSliderConfig>;
  overlayMode: boolean;
  overlayColor: string;
  noHoles: boolean;
  shapeHint: string;
}): void {
  const saved: SavedState = {
    seedX: state.seedX,
    seedY: state.seedY,
    stash: state.stash,
    stashCounter: state.stashCounter,
    tolerance: state.sliders.tolerance.el.value,
    margin: state.sliders.margin.el.value,
    rdp: state.sliders.rdp.el.value,
    segments: state.sliders.segments.el.value,
    opacity: state.sliders.opacity.el.value,
    overlayColor: state.overlayColor,
    shapeHint: state.shapeHint,
    noHoles: state.noHoles,
    gaPenalty: state.gaSliders.penalty.el.value,
    gaOvershoot: state.gaSliders.overshoot.el.value,
    gaPop: state.gaSliders.pop.el.value,
    gaGen: state.gaSliders.gen.el.value,
    overlayMode: state.overlayMode,
  };
  try { localStorage.setItem(LS_KEY, JSON.stringify(saved)); } catch (e) { console.warn('saveState:', e); }
}

/**
 * Deserialize the previously saved application state from localStorage.
 * Returns `null` when no valid snapshot exists.
 */
export function loadState(): SavedState | null {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as SavedState;
  } catch (e) { return null; }
}

/**
 * Persist the source image file, its raw RGBA pixel data, and dimensions to IndexedDB.
 * If `file` is null the file and RGBA buffers are skipped (only meta is written).
 */
export async function persistImage(file: File | null, rgbaData: Uint8ClampedArray, w: number, h: number): Promise<void> {
  if (file != null) {
    await idbPut('file', file);
    await idbPut('originalRgba', rgbaData.buffer.slice(0));
  }
  await idbPut('meta', JSON.stringify({ w, h }));
}

/**
 * Restore a previously persisted image from IndexedDB.
 * Returns `null` when any of the file, RGBA data, or meta entries are missing.
 */
export async function restoreImage(): Promise<RestoredImage | null> {
  const [file, origBuf, meta] = await Promise.all([
    idbGet('file'), idbGet('originalRgba'), idbGet('meta'),
  ]) as [File | null, ArrayBuffer | null, string | null];
  if (!file || !origBuf || !meta) return null;
  const { w, h }: { w: number; h: number } = JSON.parse(meta);
  return { file, rgba: new Uint8ClampedArray(origBuf), imgW: w, imgH: h };
}

/**
 * Persist a per-file stash list to localStorage under a scoped key.
 * Removes the key when the stash is empty.
 */
export function saveFileStash(key: string | null, stash: unknown[]): void {
  if (!key) return;
  try {
    if (stash.length > 0) {
      localStorage.setItem('svgFitStash:' + key, JSON.stringify(stash));
    } else {
      localStorage.removeItem('svgFitStash:' + key);
    }
  } catch (e) { console.warn('saveFileStash:', e); }
}

/**
 * Load a per-file stash list from localStorage.
 * Returns `null` when no stash exists for the given key.
 */
export function loadFileStash(key: string): unknown[] | null {
  try {
    const raw = localStorage.getItem('svgFitStash:' + key);
    if (raw) return JSON.parse(raw);
  } catch (e) { /* ignore */ }
  return null;
}
